import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { applyTeamRoleToAll, patchClientConnections, TEAM_ROLES, type Manager, type TeamRoleKey } from '@/data/client';
import { IqName } from '@/components/IqName';
import { apiErrMsg } from './util';
import type { ClientFormProps } from './types';
import { CONNECTION_TABS, connectionsFromClient, pickConnections } from './data';
import { useDraft } from './useDraft';
import { SaveFooter } from './SaveFooter';

const KEYS = CONNECTION_TABS.team;

/* The Triton people on this account: the content manager, then the other roles
   shown on the client's dashboard. Sits above the client's own users so the
   Team & Users tab answers one question — who is on this client — from both
   sides, without mixing the two lists. */
export function TeamTritonForm({ client, managers, onSaved, onDirtyChange }: ClientFormProps & { managers: Manager[] }) {
  const f = useDraft({
    formId: 'team',
    initial: connectionsFromClient(client),
    keys: KEYS,
    persist: (d) => patchClientConnections(client.orgId, pickConnections(d, KEYS)),
    onSaved, onDirtyChange,
  });
  const { draft: conn, set: setC } = f;
  const qc = useQueryClient();
  const [applyMsg, setApplyMsg] = useState<string | null>(null);
  const [applying, setApplying] = useState(false);
  /* One person holds the contentIQ Editor role across the whole portfolio, so
     the form can push this client's choice to every client in one click. */
  const applyAll = async (role: TeamRoleKey) => {
    const userId = conn.team[role];
    const who = managers.find((m) => m.id === userId)?.name;
    const label = TEAM_ROLES.find((r) => r.key === role)?.label ?? role;
    if (!window.confirm(userId ? `Set ${who ?? userId} as ${label} on EVERY client? Other roles are untouched.` : `Clear ${label} on EVERY client?`)) return;
    setApplying(true); setApplyMsg(null);
    try {
      const r = await applyTeamRoleToAll(role, userId);
      setApplyMsg(`${label}: updated ${r.updated} of ${r.clients} clients.`);
      qc.invalidateQueries({ queryKey: ['admin', 'clients'] });
    } catch (e) { setApplyMsg(apiErrMsg(e, 'Could not apply to all clients.')); }
    finally { setApplying(false); }
  };

  return (
    <div className="admin-form">
      <div className="admin-form__body">
        <section className="admin-sect">
          <h4 className="admin-sect__title">Team Triton</h4>
          <div className="admin-prof">
            <label className="tr-field">
              <span className="tr-field__label">Content manager</span>
              <div className="tr-field__control">
                <select
                  className="tr-select"
                  value={conn.contentManagerId}
                  onChange={(e) => setC('contentManagerId', e.target.value)}
                >
                  <option value="">Unassigned</option>
                  {managers.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
                </select>
              </div>
            </label>
            {TEAM_ROLES.map((r) => (
              <label key={r.key} className="tr-field">
                <span className="tr-field__label"><IqName text={r.label} /></span>
                <div className="tr-field__control">
                  <select className="tr-select" value={conn.team[r.key]}
                    onChange={(e) => setC('team', { ...conn.team, [r.key]: e.target.value })}>
                    <option value="">Unassigned</option>
                    {managers.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
                  </select>
                </div>
                {r.key === 'contentEditor' && (
                  <span className="tr-field__hint">
                    Takes over the content hand-off emails from the Content Manager: Expert Studio blog, case-study and knowledge-session submissions, finished AI interviews, answered gap questions, and approved blog topics. Live-interview scheduling and social posts stay with the Content Manager.{' '}
                    <button type="button" className="asst-drawer__link" disabled={applying || f.dirty} title={f.dirty ? 'Save this client first' : undefined} onClick={() => applyAll('contentEditor')}>
                      {applying ? 'Applying…' : 'Apply this choice to all clients'}
                    </button>
                  </span>
                )}
              </label>
            ))}
            {applyMsg && <p className="admin-hint" style={{ gridColumn: '1 / -1', margin: 0 }}>{applyMsg}</p>}
            <p className="admin-hint" style={{ gridColumn: '1 / -1', margin: 0 }}>Assigned team members show on the client&rsquo;s dashboard (name + photo only). Roles left unassigned are hidden.</p>
          </div>
        </section>
      </div>
      <SaveFooter dirty={f.dirty} saving={f.saving} err={f.err} onSave={f.save} label="Save team" sticky={false} />
    </div>
  );
}
